import React, { ForwardedRef, useMemo, useRef } from 'react';
import useUnitPlaneGeometry from '../geometry/useUnitPlaneGeometry';
import { GroupProps } from '@react-three/fiber/dist/declarations/src/three-types';
import { Group, Mesh, Vector3 } from 'three';
import useTextTexture, { CanvasTextStyle } from '../hooks/useTextTexture';
import LabelMaterial from '../materials/LabelMaterial';
import { useThree } from '@react-three/fiber';

type Props = GroupProps & CanvasTextStyle & {
    text?: string,
    font?: string,
    height: number,
    bgAlpha?: number,
    resolution?: number,
    labelAlign?: number[],
    fontWeight?: number | string,
    labelOffset?: number[],
    screenAlign?: boolean,
    flipSide?: boolean
};

const VectorLabel = React.forwardRef(({
  text,
  height,
  resolution,
  bgColor,
  bgAlpha,
  textColor,
  screenAlign,
  labelOffset,
  labelAlign,
  fontSize,
  padding,
  fontWeight,
  flipSide,
  font,
  ...props
}: Props, ref?: ForwardedRef<Group>) =>
{
  const meshRef = useRef<Mesh>();
  const geom = useUnitPlaneGeometry();
  const texture = useTextTexture(height, text, {
    fontSize,
    font,
    fontWeight,
    padding
  }, resolution);

  const { size } = useThree();
  const scale = useMemo(() =>
  {
    const aspect = size.width / size.height;
    return Math.min(aspect, 1);
  }, [ size.width, size.height ]);

  const width = useMemo(() => height / texture.image.nativeHeight * texture.image.nativeWidth, [ texture.image.nativeWidth, texture.image.nativeHeight, height ]);
  const labelAlignPos = useMemo(() =>
  {
    if (!labelAlign) return new Vector3();
    return new Vector3(width * labelAlign[0] * .5, height * .5 * labelAlign[1], 0);
  }, [labelAlign, width, height]);

  return (<group {...props} ref={ref}>
    <mesh geometry={geom} scale={[ width, height, 1 ]} renderOrder={500}
      position={labelAlignPos} ref={meshRef}>
      <LabelMaterial map={texture} width={width} height={height} scale={scale} align={screenAlign}
        flipSide={flipSide}
        offset={labelOffset} target={meshRef.current} bgColor={bgColor} bgAlpha={bgAlpha}
        textColor={textColor}/>
    </mesh>
  </group>);
});

VectorLabel.displayName = 'VectorLabel';

export default VectorLabel;
